import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { authAPI } from "../authAPI";
import { customerAPI } from "../customerAPI";
import { botpress, api } from "../config";
import { WEBCHAT_CONFIG } from "./webchatConfig";
import { toast } from 'react-toastify';
import PaymentModal from "./Paymentmodal";
import {
  clearPendingPayment,
  hasMatchingPendingPayment,
  storePendingPayment,
} from "../utils/paymentSession";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const INDUSTRIES = [
  "Agriculture & Agrochemicals",
  "Automotive, Aerospace & Defence",
  "Chemicals & Materials",
  "Consumer Goods & Packaging",
  "Energy, Infrastructure and Mining",
  "Food & Beverages",
  "Healthcare & Medical Devices",
  "Electronics, ICT & Semiconductors",
  "Industry Automation",
  "Logistics & Transportation",
  "Media & Entertainment",
];

// Signup stores "None" for industry, address and city until the user fills them in here
const PLACEHOLDER_PROFILE_VALUE = "None";
const isUnset = (value) => !value || value === PLACEHOLDER_PROFILE_VALUE;
const editableValue = (value) => (isUnset(value) ? "" : value);

const buildEditForm = (profile) => ({
  companyCustomerName: profile?.companyCustomerName || "",
  companyCustomerSecPhone: profile?.companyCustomerSecPhone || "",
  companyCustomerSecEmail: profile?.companyCustomerSecEmail || "",
  industry: profile?.industry || profile?.companyCustomerIndustry || "",
  companyCustomerAddress1: profile?.companyCustomerAddress1 || "",
  companyCustomerCity: profile?.companyCustomerCity || "",
});

const AccountSettings = ({ user, onClose, onLogout, isDarkMode }) => {
  const [activeSection, setActiveSection] = useState("account");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Profile Data State
  const [profileData, setProfileData] = useState(user || {});
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Usage & Billing State
  const [usageData, setUsageData] = useState(null);
  const [credits, setCredits] = useState({ total: 0, remaining: 0 });
  const [storedFiles, setStoredFiles] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [nextRenewalDate, setNextRenewalDate] = useState(null);
  const [, setActiveSubscription] = useState(null);
  const [activeSubscriptionData, setActiveSubscriptionData] = useState(null);

  // Edit State
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingUsername, setIsEditingUsername] = useState(false); // Username usually not editable in simple profile but keeping for UI consistency
  const [editForm, setEditForm] = useState(() => buildEditForm(user));
  const [isEditingBusiness, setIsEditingBusiness] = useState(false);
  const [businessForm, setBusinessForm] = useState({
    industry: "",
    customIndustry: "",
    companyCustomerAddress1: "",
    companyCustomerCity: "",
  });

  // Password Reset State
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Modals Local State
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Token
  const getToken = () => {
    if (user?.token || user?.jwt) return user.token || user.jwt;
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        return parsed.token || parsed.jwt || "";
      }
    } catch (e) {
      console.error("Error reading token from localStorage:", e);
    }
    return "";
  };
  const token = getToken();

  useEffect(() => {
    if (token) {
      loadProfile();
    }
  }, [token]);

  useEffect(() => {
    if (token && activeSection === "usages") {
      loadUsageData();
    } else if (token && activeSection === "billing") {
      loadBillingData();
    }
  }, [activeSection, token, selectedMonth, profileData]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadUsageData = async () => {
    // Get Chat User ID from localStorage if available (Critical for credits/usage APIs)
    let chatUserId = null;
    try {
      const chatSession = localStorage.getItem(WEBCHAT_CONFIG.clientName);
      if (chatSession) {
        chatUserId = JSON.parse(chatSession).userId;
      }
    } catch (e) {
      console.error("Error reading chat session", e);
    }

    // Get user ID from available sources, prioritizing Chat User ID for these specific APIs
    // The customer/credits and usage/monthly APIs expect the Chat User ID (user_...) not the Customer ID (CUST...)
    const userId = chatUserId || user?.userId || user?.id || profileData?.userId || profileData?.id;

    if (!userId) {
      return;
    }

    setLoadingUsage(true);
    try {

      const [creditsRes, usageRes, filesRes] = await Promise.all([
        customerAPI.getCredits(token, userId),
        customerAPI.getMonthlyUsage(token, selectedMonth, userId),
        customerAPI.getStoredFiles(token, userId),
      ]);

      if (creditsRes.success) setCredits(creditsRes.data.data);
      if (usageRes.success) setUsageData(usageRes.data.data);
      if (filesRes.success) {
        console.log("📁 Stored Files Response:", filesRes.data.data);
        setStoredFiles(filesRes.data.data || []);
      }
    } catch (err) {
      console.error("Error loading usage data", err);
    }
    setLoadingUsage(false);
  };

  const loadBillingData = async () => {
    setLoadingBilling(true);
    try {
      const token = user?.token || user?.jwt || user?.userToken;
      const historyRes = await customerAPI.getPaymentHistory(token);
      if (historyRes.success) {
        setPaymentHistory(historyRes.data.data || []);
        setNextRenewalDate(historyRes.data.nextRenewalDate || historyRes.data.next_renewal_date || null);
        setActiveSubscription(historyRes.data.activeSubscription || historyRes.data.active_subscription || null);
      }

      const activeRes = await customerAPI.getActiveSubscription(token);
      if (activeRes.success) {
        const subData = activeRes.data?.data || activeRes.data;
        setActiveSubscriptionData(subData);
      }
    } catch (err) {
      console.error("Error loading billing data", err);
    }
    setLoadingBilling(false);
  };

  const handleRenewPlan = async () => {
    try {
      const token = user?.token || user?.jwt || user?.userToken;
      if (!token) {
        toast.error("Please login to proceed with payment.");
        return;
      }

      const renewToastId = toast.loading("Initializing renewal...", {
        position: "top-right",
      });

      // 1. Get active subscription to get planVersionId
      const activeRes = await customerAPI.getActiveSubscription(token);
      const planVersionId = activeRes.data?.plan_version_id || activeRes.data?.data?.plan_version_id;
      if (!activeRes.success || !planVersionId) {
        toast.dismiss(renewToastId);
        toast.info("No active subscription found. Please choose a plan to subscribe.", { autoClose: 4000 });
        setShowUpgradeModal(true);
        return;
      }

      // 2. Renew Payment (calling POST to /customer/payments/renew)
      const renewRes = await customerAPI.renewSubscription(token, planVersionId, "razorpay", "INR");
      if (!renewRes.success) {
        toast.update(renewToastId, { render: renewRes.error || "Failed to create renewal order", type: "error", isLoading: false, autoClose: 3000 });
        return;
      }

      toast.dismiss(renewToastId);

      const orderData = renewRes.data;
      if (orderData.order) {
        const { id: order_id, amount, currency } = orderData.order;
        storePendingPayment({ gateway: "razorpay", orderId: order_id, source: "account-settings-renewal" });

        // Load Razorpay SDK
        if (!window.Razorpay) {
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          await new Promise((resolve) => {
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
          });
        }

        const options = {
          key: api.razorpayKey,
          amount: amount,
          currency: currency || "INR",
          name: "Infinium Intelligence",
          description: "Renew Subscription",
          order_id: order_id,
          handler: async function (response) {
            const verifyToast = toast.loading("Verifying Payment...", {
              position: "top-right"
            });
            try {
              if (!hasMatchingPendingPayment({ gateway: "razorpay", orderId: response.razorpay_order_id })) {
                toast.update(verifyToast, { render: "Payment verification rejected for this session.", type: "error", isLoading: false, autoClose: 3000 });
                return;
              }

              const verifyResult = await customerAPI.verifyPayment(token, {
                gateway: "razorpay",
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              });

              if (verifyResult.success) {
                clearPendingPayment();
                toast.update(verifyToast, { render: "Payment Successful!", type: "success", isLoading: false, autoClose: 3000 });
                setShowPaymentSuccessModal(true);
                loadBillingData();
                setTimeout(() => window.location.reload(), 4000); // Increased timeout to let user see modal
              } else {
                toast.update(verifyToast, { render: "Payment verification failed", type: "error", isLoading: false, autoClose: 3000 });
              }
            } catch (err) {
              console.error("Verification error", err);
              toast.update(verifyToast, { render: "Payment verification failed during request.", type: "error", isLoading: false, autoClose: 3000 });
            }
          },
          prefill: {
            name: user?.name || user?.firstName || profileData?.companyCustomerName || "",
            email: user?.email || profileData?.companyCustomerPriEmail || "",
            contact: user?.phone || user?.contact || profileData?.companyCustomerSecPhone || ""
          },
          theme: {
            color: "#06b6d4"
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
          toast.error(response.error.description || "Payment failed");
        });
        rzp.open();
      } else {
        toast.error("Invalid response from renewal API");
      }
    } catch (err) {
      console.error("Renewal Error:", err);
      toast.error("An error occurred during renewal.");
    }
  };

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const [result, activeSubResult] = await Promise.all([
        authAPI.getProfile(token),
        customerAPI.getActiveSubscription(token)
      ]);

      if (result.success) {
        const profile = result.data.profile || result.data;
        setProfileData(profile);
        setEditForm(buildEditForm(profile));
      } else {
        setError("Failed to load profile");
      }

      if (activeSubResult.success && activeSubResult.data?.data) {
        setActiveSubscriptionData(activeSubResult.data.data);
      }
    } catch (err) {
      console.error("Error loading profile data", err);
      setError("Failed to load profile");
    }
    setIsLoading(false);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type and size (e.g., max 5MB)
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File size should be less than 5MB");
      return;
    }

    try {
      setIsUploading(true);
      clearMessages();

      // Step 1: Get presigned URL
      const key = `uploads/${Date.now()}_${file.name}`;
      const step1Response = await fetch(`${botpress.apiUrl}/files`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${botpress.token}`,
          "x-bot-id": botpress.botId,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          key: key,
          size: file.size,
          accessPolicies: ["public_content"]
        })
      });

      if (!step1Response.ok) {
        throw new Error("Failed to initiate upload");
      }

      const step1Data = await step1Response.json();
      const uploadUrl = step1Data.uploadUrl;
      const publicUrl = step1Data.url || step1Data.publicUrl; // Assuming response contains the public URL

      // Step 2: Upload file to presigned URL
      const step2Response = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type
        },
        body: file
      });

      if (!step2Response.ok) {
        throw new Error("Failed to upload file");
      }

      setSuccess("Avatar uploaded successfully!");
      // Optimistically update avatar if we have a URL, or fetch profile again
      // For now, let's assume valid upload and maybe refetch profile? 
      // Or if possible, update backend with new avatar URL? The user prompt didn't specify updating backend, 
      // but usually we want to associate it.
      // Since I don't know the backend API to update avatar URL specific field, I will just show success.
      // If the publicUrl is available, we could assume that's the new avatar.

      console.log("Uploaded Avatar URL:", publicUrl);

    } catch (err) {
      console.error("Upload error:", err);
      setError("Failed to upload avatar: " + err.message);
    } finally {
      setIsUploading(false);
      // Reset input
      e.target.value = null;
    }
  };

  const menuItems = [
    { id: "account", label: "Account", icon: "user" },
    { id: "password-reset", label: "Reset Password", icon: "lock" },
    { id: "billing", label: "Billing", icon: "credit-card" },
    { id: "usages", label: "Usages", icon: "chart" },
  ];

  const workspaceItems = [
    { id: "enterprise", label: "Enterprise", icon: "building", external: true },
  ];

  const renderIcon = (iconName) => {
    const icons = {
      user: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
      sliders: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
          />
        </svg>
      ),
      palette: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
          />
        </svg>
      ),
      bot: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      ),
      checklist: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
      ),
      bell: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
      ),
      plug: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      ),
      building: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
      folder: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
          />
        </svg>
      ),
      users: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
      "credit-card": (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
      ),
      chart: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
      key: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
          />
        </svg>
      ),
      code: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
          />
        </svg>
      ),
      lock: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      ),
    };
    return icons[iconName] || icons.user;
  };

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleUpdateProfile = async (fieldToUpdate = null) => {
    setIsLoading(true);
    clearMessages();

    // If fieldToUpdate is passed (e.g., 'companyCustomerName'), we can selectively update if api supports it
    // But authAPI.updateProfile takes the whole object usually.
    // So we use editForm state.

    const result = await authAPI.updateProfile(token, editForm);

    if (result.success) {
      setSuccess("Profile updated successfully!");
      setProfileData({ ...profileData, ...editForm });
      setIsEditingName(false);
      setIsEditingUsername(false);
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const startEditingBusiness = () => {
    const industry = editableValue(editForm.industry);
    const isKnownIndustry = !industry || INDUSTRIES.includes(industry);
    setBusinessForm({
      industry: isKnownIndustry ? industry : "Others",
      customIndustry: isKnownIndustry ? "" : industry,
      companyCustomerAddress1: editableValue(editForm.companyCustomerAddress1),
      companyCustomerCity: editableValue(editForm.companyCustomerCity),
    });
    clearMessages();
    setIsEditingBusiness(true);
  };

  const handleSaveBusinessDetails = async () => {
    setIsLoading(true);
    clearMessages();

    const industry = businessForm.industry === "Others"
      ? businessForm.customIndustry.trim()
      : businessForm.industry;
    const updatedForm = {
      ...editForm,
      industry: industry || PLACEHOLDER_PROFILE_VALUE,
      companyCustomerAddress1: businessForm.companyCustomerAddress1.trim() || PLACEHOLDER_PROFILE_VALUE,
      companyCustomerCity: businessForm.companyCustomerCity.trim() || PLACEHOLDER_PROFILE_VALUE,
    };

    const result = await authAPI.updateProfile(token, updatedForm);

    if (result.success) {
      setSuccess("Profile updated successfully!");
      setEditForm(updatedForm);
      setProfileData({ ...profileData, ...updatedForm });
      setIsEditingBusiness(false);
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const renderAccountSection = () => (
    <div className="space-y-8">
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept="image/*"
      />

      {/* User Profile */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div
            className="relative w-16 h-16 group cursor-pointer"
            onClick={handleAvatarClick}
          >
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
              {profileData.avatarUrl ? (
                <img src={profileData.avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                profileData.companyCustomerName?.charAt(0).toUpperCase() || "U"
              )}
            </div>

            {/* Overlay */}
            <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
          </div>
          <div>
            <h3
              className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"
                }`}
            >
              {(profileData.companyCustomerName || "").toUpperCase()}
            </h3>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
            >
              {profileData.companyCustomerPriEmail}
            </p>
          </div>
        </div>
        <button
          onClick={handleAvatarClick}
          disabled={isUploading}
          className={`px-4 py-2 text-sm rounded-lg border ${isDarkMode
            ? "border-gray-700 text-gray-300 hover:bg-gray-800"
            : "border-gray-300 text-gray-700 hover:bg-gray-50"
            } transition-colors flex items-center gap-2`}
        >
          {isUploading ? (
            <span>Uploading...</span>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Change avatar
            </>
          )}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500">
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-green-500">
          {success}
        </div>
      )}

      {/* Full Name */}
      <div className="space-y-2">
        <label
          className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
        >
          Full Name
        </label>
        {isEditingName ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={editForm.companyCustomerName}
              onChange={(e) => setEditForm({ ...editForm, companyCustomerName: e.target.value })}
              className={`flex-1 px-4 py-2 rounded-lg border ${isDarkMode
                ? "bg-gray-800 border-gray-700 text-white"
                : "bg-white border-gray-300 text-gray-900"
                } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
            />
            <button
              onClick={() => handleUpdateProfile('companyCustomerName')}
              className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
            >
              Save
            </button>
            <button
              onClick={() => {
                setIsEditingName(false);
                setEditForm({ ...editForm, companyCustomerName: profileData.companyCustomerName }); // Revert
              }}
              className={`px-4 py-2 rounded-lg border ${isDarkMode ? "border-gray-700 text-gray-300" : "border-gray-300"}`}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              {(profileData.companyCustomerName || "").toUpperCase()}
            </p>
            <button
              onClick={() => setIsEditingName(true)}
              className={`text-sm ${isDarkMode
                ? "text-cyan-400 hover:text-cyan-300"
                : "text-cyan-600 hover:text-cyan-700"
                }`}
            >
              Change full name
            </button>
          </div>
        )}
      </div>

      {/* Secondary Info (Used to be Username in mock, now using secondary fields from Profile) */}
      <div className="space-y-2">
        <label
          className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
        >
          Secondary Phone
        </label>
        {isEditingUsername ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={editForm.companyCustomerSecPhone}
              onChange={(e) => setEditForm({ ...editForm, companyCustomerSecPhone: e.target.value })}
              className={`flex-1 px-4 py-2 rounded-lg border ${isDarkMode
                ? "bg-gray-800 border-gray-700 text-white"
                : "bg-white border-gray-300 text-gray-900"
                } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
            />
            <button
              onClick={() => handleUpdateProfile()}
              className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
            >
              Save
            </button>
            <button
              onClick={() => {
                setIsEditingUsername(false);
                setEditForm({ ...editForm, companyCustomerSecPhone: profileData.companyCustomerSecPhone });
              }}
              className={`px-4 py-2 rounded-lg border ${isDarkMode ? "border-gray-700 text-gray-300" : "border-gray-300"}`}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              {profileData.companyCustomerSecPhone || "Not set"}
            </p>
            <button
              onClick={() => setIsEditingUsername(true)}
              className={`text-sm ${isDarkMode
                ? "text-cyan-400 hover:text-cyan-300"
                : "text-cyan-600 hover:text-cyan-700"
                }`}
            >
              Change Phone
            </button>
          </div>
        )}
      </div>

      {/* Email */}
      <div className="space-y-2">
        <label
          className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
        >
          Primary Email
        </label>
        <p className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
          {profileData.companyCustomerPriEmail}
        </p>
      </div>

      {/* Business Details & Address */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3
            className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"
              }`}
          >
            Business Details
          </h3>
          {!isEditingBusiness && (
            <button
              onClick={startEditingBusiness}
              className={`text-sm ${isDarkMode
                ? "text-cyan-400 hover:text-cyan-300"
                : "text-cyan-600 hover:text-cyan-700"
                }`}
            >
              Edit details
            </button>
          )}
        </div>
        <div className={`p-4 md:p-6 rounded-lg border ${isDarkMode
          ? "bg-gray-800/50 border-gray-700"
          : "bg-gray-50 border-gray-200"
          }`}>
          {isEditingBusiness ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="space-y-1">
                  <label className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}>
                    Industry
                  </label>
                  <select
                    value={businessForm.industry}
                    onChange={(e) => setBusinessForm({ ...businessForm, industry: e.target.value })}
                    className={`w-full px-4 py-2 rounded-lg border ${isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                      } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                  >
                    <option value="">Select your industry</option>
                    {INDUSTRIES.map((industry) => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                    <option value="Others">Others</option>
                  </select>
                </div>
                {businessForm.industry === "Others" && (
                  <div className="space-y-1">
                    <label className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}>
                      Specify Industry
                    </label>
                    <input
                      type="text"
                      value={businessForm.customIndustry}
                      onChange={(e) => setBusinessForm({ ...businessForm, customIndustry: e.target.value })}
                      placeholder="Enter your industry"
                      className={`w-full px-4 py-2 rounded-lg border ${isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                        } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                    />
                  </div>
                )}
                <div className="space-y-1 lg:col-span-2">
                  <label className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}>
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={businessForm.companyCustomerAddress1}
                    onChange={(e) => setBusinessForm({ ...businessForm, companyCustomerAddress1: e.target.value })}
                    placeholder="Street address"
                    className={`w-full px-4 py-2 rounded-lg border ${isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                      } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                  />
                </div>
                <div className="space-y-1">
                  <label className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}>
                    City
                  </label>
                  <input
                    type="text"
                    value={businessForm.companyCustomerCity}
                    onChange={(e) => setBusinessForm({ ...businessForm, companyCustomerCity: e.target.value })}
                    placeholder="Your city"
                    className={`w-full px-4 py-2 rounded-lg border ${isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                      } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveBusinessDetails}
                  disabled={isLoading}
                  className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50"
                >
                  {isLoading ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={() => setIsEditingBusiness(false)}
                  disabled={isLoading}
                  className={`px-4 py-2 rounded-lg border ${isDarkMode ? "border-gray-700 text-gray-300" : "border-gray-300"}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <label className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}>
                  Industry
                </label>
                <p className={`mt-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  {isUnset(editForm.industry) ? "Not set" : editForm.industry}
                </p>
              </div>
              <div>
                <label className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}>
                  City
                </label>
                <p className={`mt-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  {isUnset(profileData.companyCustomerCity) ? "Not set" : profileData.companyCustomerCity}
                </p>
              </div>
              <div className="lg:col-span-2">
                <label className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}>
                  Street Address
                </label>
                <p className={`mt-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  {[profileData.companyCustomerAddress1, profileData.companyCustomerAddress2, profileData.companyCustomerAddress3]
                    .filter((line) => !isUnset(line))
                    .join(", ") || "Not set"}
                </p>
              </div>
              {profileData.companyCustomerState && (
                <div>
                  <label className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}>
                    State
                  </label>
                  <p className={`mt-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                    {profileData.companyCustomerState}
                  </p>
                </div>
              )}
              {profileData.companyCustomerCountry && (
                <div>
                  <label className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}>
                    Country
                  </label>
                  <p className={`mt-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                    {profileData.companyCustomerCountry}
                  </p>
                </div>
              )}
              {profileData.companyCustomerPincode && (
                <div>
                  <label className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}>
                    Pincode
                  </label>
                  <p className={`mt-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                    {profileData.companyCustomerPincode}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Subscription */}
      <div
        className={`border-t ${isDarkMode ? "border-gray-800" : "border-gray-200"
          } pt-8`}
      >
        <h3
          className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"
            }`}
        >
          Your Subscription
        </h3>
        <div
          className={`p-4 rounded-lg border ${isDarkMode
            ? "bg-gray-800/50 border-gray-700"
            : "bg-gray-50 border-gray-200"
            }`}
        >
          <div className="flex items-start justify-between mb-2">
            <div>
              <p
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"
                  }`}
              >
                Plan Status
              </p>
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                {activeSubscriptionData?.plan_versions?.display_name || (profileData.currentPlan ? profileData.currentPlan.replace(/_/g, " ").toUpperCase() : (profileData.role || "Standard User"))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* System */}
      <div
        className={`border-t ${isDarkMode ? "border-gray-800" : "border-gray-200"
          } pt-8`}
      >
        <h3
          className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"
            }`}
        >
          System
        </h3>

        {/* Sign out */}
        <div className="flex items-center justify-between py-3">
          <p className={`${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
            You are signed in as {profileData.companyCustomerName}
          </p>
          <button
            onClick={() => {
              if (onLogout) {
                onLogout();
              }
              if (onClose) {
                onClose();
              }
            }}
            className={`px-4 py-2 text-sm rounded-lg border ${isDarkMode
              ? "border-gray-700 text-gray-300 hover:bg-gray-800"
              : "border-gray-300 text-gray-700 hover:bg-gray-50"
              } transition-colors`}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );

  const renderPasswordResetSection = () => {
    const handlePasswordReset = async (e) => {
      e.preventDefault();
      clearMessages();

      if (!token) {
        setError("Authentication session invalid. Please sign out and sign in again.");
        return;
      }

      if (newPassword !== confirmPassword) {
        setError("New passwords do not match");
        return;
      }

      setIsLoading(true);

      const result = await authAPI.changePassword(
        token,
        profileData.companyCustomerPriEmail || profileData.email,
        currentPassword,
        newPassword,
        confirmPassword
      );

      if (result.success) {
        setSuccess("Password changed successfully!");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setError(result.error);
      }
      setIsLoading(false);
    };

    return (
      <div className="space-y-8">
        <div className="max-w-2xl">
          <p
            className={`mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            Update your password to keep your account secure. Make sure to use a
            strong password with a mix of letters, numbers, and symbols.
          </p>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-green-500">
              {success}
            </div>
          )}

          <form onSubmit={handlePasswordReset} className="space-y-6">
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your current password"
                  className={`w-full px-4 py-3 pr-12 rounded-lg border ${isDarkMode
                    ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 ${isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-500 hover:text-gray-700"
                    } transition-colors`}
                >
                  {showCurrentPassword ? (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      ></path>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      ></path>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter your new password"
                  className={`w-full px-4 py-3 pr-12 rounded-lg border ${isDarkMode
                    ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 ${isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-500 hover:text-gray-700"
                    } transition-colors`}
                >
                  {showNewPassword ? (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      ></path>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      ></path>
                    </svg>
                  )}
                </button>
              </div>
              <p
                className={`mt-2 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
              >
                Password must be at least 8 characters long
              </p>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  className={`w-full px-4 py-3 pr-12 rounded-lg border ${isDarkMode
                    ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 ${isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-500 hover:text-gray-700"
                    } transition-colors`}
                >
                  {showConfirmPassword ? (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      ></path>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      ></path>
                    </svg>
                  )}
                </button>
              </div>
              {newPassword &&
                confirmPassword &&
                newPassword !== confirmPassword && (
                  <p className="mt-2 text-xs text-red-500">
                    Passwords do not match
                  </p>
                )}
            </div>

            <div className="flex items-center gap-3 pt-4">
              <button
                type="submit"
                disabled={
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword ||
                  newPassword !== confirmPassword
                }
                className={`px-6 py-3 rounded-lg font-medium transition-all ${currentPassword &&
                  newPassword &&
                  confirmPassword &&
                  newPassword === confirmPassword
                  ? "bg-cyan-500 hover:bg-cyan-600 text-white cursor-pointer"
                  : isDarkMode
                    ? "bg-gray-800 text-gray-500 cursor-not-allowed"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
              >
                {isLoading ? "Updating.." : "Update Password"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                className={`px-6 py-3 rounded-lg font-medium border transition-all ${isDarkMode
                  ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        <div
          className={`max-w-2xl p-4 md:p-6 rounded-xl border ${isDarkMode
            ? "bg-gray-800/50 border-gray-700"
            : "bg-gray-50 border-gray-200"
            }`}
        >
          <h3
            className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"
              }`}
          >
            <svg
              className="w-5 h-5 text-cyan-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            Password Security Tips
          </h3>
          <ul
            className={`space-y-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
          >
            <li className="flex items-start gap-2">
              <span className="text-cyan-500 mt-1">â€¢</span>
              <span>
                Use at least 8 characters with a mix of uppercase, lowercase,
                numbers, and symbols
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-500 mt-1">â€¢</span>
              <span>Avoid using common words or personal information</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-500 mt-1">â€¢</span>
              <span>Don't reuse passwords from other accounts</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-500 mt-1">â€¢</span>
              <span>
                Consider using a password manager to generate and store strong
                passwords
              </span>
            </li>
          </ul>
        </div>
      </div>
    );
  };

  const renderUsagesSection = () => {
    // Chart Data Preparation
    // API returns 'dailyUsage' object { "YYYY-MM-DD": credits }
    const byDay = usageData?.dailyUsage || {};
    const sortedDates = Object.keys(byDay).sort();

    // Calculate derived statistics
    const activeDays = sortedDates.length;
    const daysInMonth = new Date(selectedMonth.split('-')[0], selectedMonth.split('-')[1], 0).getDate();
    const inactiveDays = Math.max(0, daysInMonth - activeDays);

    const totalCredits = usageData?.summary?.totalCreditsUsed || 0;
    const avgCreditsPerDay = activeDays > 0 ? totalCredits / activeDays : 0;

    // Find Peak Activity
    let peakDay = '-';
    let peakCredits = 0;

    if (activeDays > 0) {
      const peakDate = sortedDates.reduce((a, b) => byDay[a] > byDay[b] ? a : b);
      peakDay = new Date(peakDate).toLocaleDateString();
      peakCredits = byDay[peakDate];
    }

    // Determine Top Action
    const usageByAction = usageData?.summary?.usageByAction || {};
    let topAction = '-';
    let maxActionCount = 0;

    Object.entries(usageByAction).forEach(([action, count]) => {
      if (count > maxActionCount) {
        maxActionCount = count;
        topAction = action;
      }
    });

    const chartData = {
      labels: sortedDates.map(date => new Date(date).toLocaleDateString()),
      datasets: [
        {
          label: "Usage (Credits)",
          data: sortedDates.map(date => byDay[date]),
          backgroundColor: isDarkMode ? "rgba(34, 211, 238, 0.5)" : "rgba(6, 182, 212, 0.5)",
          borderColor: isDarkMode ? "rgba(34, 211, 238, 1)" : "rgba(6, 182, 212, 1)",
          borderWidth: 1,
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      plugins: {
        legend: {
          position: "top",
          labels: {
            color: isDarkMode ? "#9ca3af" : "#4b5563",
          },
        },
        title: {
          display: true,
          text: "Usage History",
          color: isDarkMode ? "#fff" : "#111",
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
          },
          ticks: {
            color: isDarkMode ? "#9ca3af" : "#4b5563",
          },
        },
        x: {
          grid: {
            color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
          },
          ticks: {
            color: isDarkMode ? "#9ca3af" : "#4b5563",
          },
        },
      },
    };

    return (
      <div className="space-y-6">
        <div>
          <p className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            Track your credit usage and file storage.
          </p>
        </div>

        {/* Month Picker */}
        <div className="flex items-center gap-4">
          <label className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
            Select Month:
          </label>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className={`px-4 py-2 rounded-lg border ${isDarkMode
              ? "bg-gray-800 border-gray-700 text-white"
              : "bg-white border-gray-300 text-gray-900"
              } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
            max={new Date().toISOString().slice(0, 7)}
          />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          <div
            className={`p-4 md:p-6 rounded-xl border ${isDarkMode
              ? "bg-gray-800/50 border-gray-700"
              : "bg-white border-gray-200"
              }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                Remaining Credits
              </span>
              <svg
                className="w-5 h-5 text-cyan-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                ></path>
              </svg>
            </div>
            <p
              className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"
                }`}
            >
              {loadingUsage ? "..." : (credits.creditsRemaining || credits.remaining || 0).toLocaleString()}
            </p>
            <p
              className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-500"
                }`}
            >
              Plan: {credits.plan || "Standard"}
            </p>
          </div>

          <div
            className={`p-4 md:p-6 rounded-xl border ${isDarkMode
              ? "bg-gray-800/50 border-gray-700"
              : "bg-white border-gray-200"
              }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                Monthly Usage
              </span>
              <svg
                className="w-5 h-5 text-orange-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                ></path>
              </svg>
            </div>
            <p className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              {loadingUsage ? "..." : (usageData?.summary?.totalCreditsUsed || 0).toLocaleString()}
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
              Avg: {avgCreditsPerDay.toFixed(1)} / day
            </p>
          </div>

          <div
            className={`p-4 md:p-6 rounded-xl border ${isDarkMode
              ? "bg-gray-800/50 border-gray-700"
              : "bg-white border-gray-200"
              }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                Active Days
              </span>
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              {loadingUsage ? "..." : activeDays}
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
              Inactive: {inactiveDays}
            </p>
          </div>

          <div
            className={`p-4 md:p-6 rounded-xl border ${isDarkMode
              ? "bg-gray-800/50 border-gray-700"
              : "bg-white border-gray-200"
              }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                Events
              </span>
              <svg
                className="w-5 h-5 text-purple-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                ></path>
              </svg>
            </div>
            <p className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              {loadingUsage ? "..." : (usageData?.summary?.totalEvents || 0).toLocaleString()}
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
              Top: {topAction}
            </p>
          </div>

          <div
            className={`p-4 md:p-6 rounded-xl border ${isDarkMode
              ? "bg-gray-800/50 border-gray-700"
              : "bg-white border-gray-200"
              }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                Peak Activity
              </span>
              <svg
                className="w-5 h-5 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <p className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              {loadingUsage ? "..." : peakDay}
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
              {peakCredits} Credits
            </p>
          </div>

          <div
            className={`p-4 md:p-6 rounded-xl border ${isDarkMode
              ? "bg-gray-800/50 border-gray-700"
              : "bg-white border-gray-200"
              }`}
          >
            {/* Placeholder for other stats or just empty for now */}
            <div className="flex items-center justify-between mb-2">
              <span
                className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                Stored Files
              </span>
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v8a2 2 0 01-2 2H5z" />
              </svg>
            </div>
            <p className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              {loadingUsage ? "..." : storedFiles.length}
            </p>
          </div>
        </div>

        {/* Chart */}
        <div
          className={`p-4 md:p-6 rounded-xl border ${isDarkMode
            ? "bg-gray-800/50 border-gray-700"
            : "bg-white border-gray-200"
            }`}
        >
          {loadingUsage ? <p>Loading chart...</p> : <Bar options={chartOptions} data={chartData} />}
        </div>

        {/* Stored Files Table */}
        <div
          className={`rounded-xl border ${isDarkMode
            ? "bg-gray-800/50 border-gray-700"
            : "bg-white border-gray-200"
            } overflow-hidden`}
        >
          <div className="p-4 md:p-6 border-b border-gray-700">
            <h3
              className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"
                }`}
            >
              Stored Files Tracking
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={isDarkMode ? "bg-gray-800" : "bg-gray-50"}>
                <tr>
                  <th className={`px-3 py-2 md:px-6 md:py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>File Name</th>
                  <th className={`px-3 py-2 md:px-6 md:py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Type</th>
                  <th className={`px-3 py-2 md:px-6 md:py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Source</th>
                  <th className={`px-3 py-2 md:px-6 md:py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>URL</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {storedFiles.map((file, idx) => (
                  <tr key={idx} className={isDarkMode ? "hover:bg-gray-800/50" : "hover:bg-gray-50"}>
                    <td className={`px-3 py-2 md:px-6 md:py-4 whitespace-nowrap ${isDarkMode ? "text-white" : "text-gray-900"}`}>{file.title}</td>
                    <td className={`px-3 py-2 md:px-6 md:py-4 whitespace-nowrap ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>{file.fileType || file.file_type}</td>
                    <td className={`px-3 py-2 md:px-6 md:py-4 whitespace-nowrap ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>{file.source}</td>
                    <td className={`px-3 py-2 md:px-6 md:py-4 whitespace-nowrap ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                      {(file.fileUrl || file.file_url) ? (
                        <a href={file.fileUrl || file.file_url} target="_blank" rel="noreferrer" className="text-cyan-500 hover:underline">View</a>
                      ) : (
                        <span className="text-gray-500">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
                {storedFiles.length === 0 && !loadingUsage && (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500">No files found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderBillingSection = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <h2 className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>Payment History</h2>
          {nextRenewalDate && (
            <p className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              Next Renewal: {new Date(nextRenewalDate).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="flex gap-4 mb-4 items-center">
          <button
            onClick={() => setShowUpgradeModal(true)}
            className={`px-6 py-2.5 rounded-xl border text-sm font-bold transition-all duration-300 transform hover:-translate-y-0.5 flex-shrink-0 flex items-center gap-2 
              bg-white text-cyan-500 border-cyan-300 hover:bg-cyan-50 hover:border-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:shadow-[0_0_20px_rgba(6,182,212,0.8)]
              ${isDarkMode ? "ring-2 ring-cyan-400" : "ring-1 ring-cyan-200"}`}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Upgrade
          </button>
          {/* Show Renew button but disable it if on Free Plan */}
          <div className="relative group">
            <button
              onClick={handleRenewPlan}
              disabled={isLoading || (activeSubscriptionData?.plan_versions?.role === 'free_plan' || profileData?.currentPlan === 'free_plan')}
              className={`px-6 py-2.5 rounded-xl text-sm font-bold border transform transition-all duration-300 relative overflow-hidden flex-shrink-0
                ${(activeSubscriptionData?.plan_versions?.role === 'free_plan' || profileData?.currentPlan === 'free_plan')
                  ? `cursor-not-allowed opacity-50 ${isDarkMode ? "bg-gray-800 text-gray-500 border-gray-700" : "bg-gray-100 text-gray-400 border-gray-200"}`
                  : `hover:-translate-y-0.5 hover:scale-[1.02] group-hover:bg-emerald-50 bg-white text-emerald-500 border-emerald-300 hover:border-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.5)] hover:shadow-[0_0_20px_rgba(16,185,129,0.8)]
                    ${isDarkMode ? "ring-2 ring-emerald-400" : "ring-1 ring-emerald-200"}`
                }
                ${isLoading ? "opacity-50 cursor-not-allowed transform-none hover:scale-100 shadow-none ring-0" : ""
                }`}>
              {!(activeSubscriptionData?.plan_versions?.role === 'free_plan' || profileData?.currentPlan === 'free_plan') && (
                <div className={`absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out rounded-xl ${isDarkMode ? "bg-emerald-100/10" : "bg-emerald-100/50"}`}></div>
              )}
              <span className="relative z-10 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {isLoading ? "Loading..." : "Renew"}
              </span>
            </button>
            {(activeSubscriptionData?.plan_versions?.role === 'free_plan' || profileData?.currentPlan === 'free_plan') && (
              <div
                className={`absolute left-1/2 top-full mt-2 -translate-x-1/2 px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 ${isDarkMode ? "bg-gray-700 text-white" : "bg-gray-900 text-white"
                  }`}
              >
                Please upgrade your plan first to renew
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className={`rounded-xl border ${isDarkMode
          ? "bg-gray-800/50 border-gray-700"
          : "bg-white border-gray-200"
          } overflow-hidden`}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={isDarkMode ? "bg-gray-800" : "bg-gray-50"}>
              <tr>
                <th className={`px-3 py-2 md:px-6 md:py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Status</th>
                <th className={`px-3 py-2 md:px-6 md:py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Amount</th>
                <th className={`px-3 py-2 md:px-6 md:py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Date</th>
                <th className={`px-3 py-2 md:px-6 md:py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Order ID</th>
                <th className={`px-3 py-2 md:px-6 md:py-3 text-center text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Invoice</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {paymentHistory.map((payment, idx) => (
                <tr key={idx} className={isDarkMode ? "hover:bg-gray-800/50" : "hover:bg-gray-50"}>
                  <td className={`px-3 py-2 md:px-6 md:py-4 whitespace-nowrap`}>
                    <span className={`px-2 py-1 rounded text-xs ${payment.status === "captured" || payment.status === "paid" || payment.status === "success" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                      {payment.status || "Pending"}
                    </span>
                  </td>
                  <td className={`px-3 py-2 md:px-6 md:py-4 whitespace-nowrap ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {payment.amount ? payment.amount.toLocaleString() : "0.00"} {payment.currency || "INR"}
                  </td>
                  <td className={`px-3 py-2 md:px-6 md:py-4 whitespace-nowrap ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                    {new Date(payment.created_at).toLocaleDateString()}
                  </td>
                  <td className={`px-3 py-2 md:px-6 md:py-4 whitespace-nowrap ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                    {payment.order_id}
                  </td>
                  <td className={`px-3 py-2 md:px-6 md:py-4 whitespace-nowrap text-center`}>
                    {payment.invoice_path ? (
                      <a
                        href={payment.invoice_path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center justify-center p-2 rounded-lg transition-colors ${isDarkMode
                          ? "text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                          : "text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                          }`}
                        title="Download Invoice"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </a>
                    ) : (
                      <span className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>-</span>
                    )}
                  </td>
                </tr>
              ))}
              {paymentHistory.length === 0 && !loadingBilling && (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No payment history found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderComingSoon = () => (
    <div className="flex flex-col items-center justify-center h-full">
      <div
        className={`text-6xl mb-4 ${isDarkMode ? "text-gray-700" : "text-gray-300"
          }`}
      >
        ðŸš§
      </div>
      <h3
        className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
      >
        Coming Soon
      </h3>
      <p className={`${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
        This feature is under development
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`${isDarkMode ? "bg-gray-950" : "bg-white"
          } rounded-2xl w-full max-w-6xl h-full md:h-[90vh] flex flex-col md:flex-row overflow-hidden shadow-2xl relative`}
      >
        {/* Mobile Backdrop */}
        {isMobileMenuOpen && (
          <div
            className="absolute inset-0 z-20 bg-black/50 backdrop-blur-sm md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar Navigation - Drawer on Mobile */}
        <div
          className={`absolute md:static inset-y-0 left-0 z-30 w-64 transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            } md:translate-x-0 ${isDarkMode
              ? "bg-gray-900 border-r border-gray-800"
              : "bg-gray-50 border-r border-gray-200"
            } p-6 overflow-y-auto flex flex-col h-full shadow-2xl md:shadow-none`}
        >
          {/* Mobile Close Button */}
          <div className="flex justify-end md:hidden mb-4">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className={`p-2 rounded-lg ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900"}`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <Link
            to="/dashboard"
            onClick={onClose}
            className={`flex items-center gap-2 mb-8 ${isDarkMode
              ? "text-gray-400 hover:text-white"
              : "text-gray-500 hover:text-gray-900"
              } transition-colors px-2`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Dashboard
          </Link>

          {/* Account Section */}
          <div className="mb-8">
            <h3
              className={`text-xs font-semibold uppercase mb-3 ${isDarkMode ? "text-gray-500" : "text-gray-500"
                }`}
            >
              Account
            </h3>
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSection(item.id);
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-colors ${activeSection === item.id
                  ? isDarkMode
                    ? "bg-gray-800 text-white"
                    : "bg-white text-gray-900 shadow-sm"
                  : isDarkMode
                    ? "text-gray-400 hover:text-white hover:bg-gray-800/50"
                    : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                  }`}
              >
                {renderIcon(item.icon)}
                <span className="text-sm">{item.label}</span>
              </button>
            ))}
          </div>

          {/* Workspace Section */}
          <div className="mb-8">
            <h3
              className={`text-xs font-semibold uppercase mb-3 ${isDarkMode ? "text-gray-500" : "text-gray-500"
                }`}
            >
              Workspace
            </h3>
            {workspaceItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSection(item.id);
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-colors ${activeSection === item.id
                  ? isDarkMode
                    ? "bg-gray-800 text-white"
                    : "bg-white text-gray-900 shadow-sm"
                  : isDarkMode
                    ? "text-gray-400 hover:text-white hover:bg-gray-800/50"
                    : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                  }`}
              >
                {renderIcon(item.icon)}
                <span className="text-sm flex-1 text-left">{item.label}</span>
                {item.external && (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto flex flex-col h-full w-full">
          {/* Mobile Header */}
          <div className={`md:hidden flex items-center justify-between p-4 border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}>
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className={`p-2 rounded-lg ${isDarkMode ? "text-gray-400 hover:bg-gray-800" : "text-gray-600 hover:bg-gray-100"}`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <span className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              {menuItems.find((item) => item.id === activeSection)?.label || "Account"}
            </span>
            <div className="w-10"></div> {/* Spacer for centering */}
          </div>

          <div className="p-4 md:p-6 lg:p-12">
            <h1
              className={`hidden md:block text-3xl font-semibold mb-8 ${isDarkMode ? "text-white" : "text-gray-900"
                }`}
            >
              {menuItems.find((item) => item.id === activeSection)?.label ||
                "Account"}
            </h1>

            {activeSection === "account"
              ? renderAccountSection()
              : activeSection === "password-reset"
                ? renderPasswordResetSection()
                : activeSection === "usages"
                  ? renderUsagesSection()
                  : activeSection === "billing"
                    ? renderBillingSection()
                    : renderComingSoon()}
          </div>
        </div>
      </div>

      {/* Payment Success Modal */}
      {showPaymentSuccessModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={`relative w-full max-w-sm rounded-2xl shadow-2xl p-6 text-center transform transition-all scale-100 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Payment Successful!
            </h3>
            <p className={`text-sm mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              Your plan has been renewed successfully. Thank you for your continued support!
            </p>
            <button
              onClick={() => {
                setShowPaymentSuccessModal(false);
                window.location.reload();
              }}
              className="w-full px-4 py-2 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-colors"
            >
              Okay
            </button>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <PaymentModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        isDarkMode={isDarkMode}
        user={user}
        currentPlan={activeSubscriptionData?.plan_versions?.role || profileData?.currentPlan || "free"}
        remainingTokens={credits.remaining}
        totalTokens={credits.total}
        isUpgrade={true}
        activePlanVersionId={activeSubscriptionData?.plan_version_id}
      />
    </div>
  );
};

export default AccountSettings;
